// E-Fatura Yönetimi sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'documents.eInvoice';

export const eInvoiceGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'E-Fatura Yönetimi nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Gelen ve giden e-faturalarınızı entegratörünüzden (EDM, PavoPay, Finansbank) okuyup tek ekranda görürsünüz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık, kontör ve ⋮ menüsü'),
    filters: t(`${G}.regions.filters`, 'Arama, tarih ve şube'),
    toolbar: t(`${G}.regions.toolbar`, 'Gelen / Giden sekmeleri'),
    table: t(`${G}.regions.table`, 'E-fatura listesi'),
    pagination: t(`${G}.regions.pagination`, 'Sayfalama'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Bu sayfa ne işe yarar?'),
      text: t(`${G}.s1.text`, 'Tedarikçilerden gelen ve sizin gönderdiğiniz e-faturalar, aktif e-fatura entegrasyonunuzdan okunur. Burada faturayı açar, indirir, yazdırır, durumunu sorgular ve gelen faturayı alış faturası olarak içeri aktarırsınız.'),
      tips: [
        t(`${G}.s1.tip1`, 'Kalan Kontör (EDM): hesabınızda kalan e-fatura/e-arşiv kontörünü gösterir; tıklayınca yenilenir. 100\'ün altına düşünce turuncu görünür.'),
        t(`${G}.s1.tip2`, '"Kullanıcı Sorgula": bir VKN / TCKN\'nin e-fatura kullanıcısı olup olmadığını sorgular.'),
        t(`${G}.s1.tip3`, 'PavoPay kullanıyorsanız: şube müdürleri kendi şubesinin PavoPay entegrasyonunu kullanır; yönetici için şubesiz (genel) bir PavoPay entegrasyonu tanımlı olmalıdır.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s2.title`, '2. ⋮ menüsü: ayarlar burada'),
      text: t(`${G}.s2.text`, 'Başlıktaki ⋮ düğmesi ek işlemleri açar. "Excel İndir" ekrandaki listeyi dosyaya aktarır (liste boşken kapalıdır).'),
      tips: [
        t(`${G}.s2.tip1`, 'Firma Bilgileri: faturada görünen firma bilgilerinizi düzenler.'),
        t(`${G}.s2.tip2`, 'Entegrasyon Ayarları: Finansbank, EDM ve PavoPay kullanıcı bilgilerini girip entegrasyonu aktif edersiniz.'),
        t(`${G}.s2.tip3`, 'Tasarım Şablonları ve PavoPay Entegrasyonları da bu menüdedir.'),
        t(`${G}.s2.tip4`, 'Şube müdürleri bu menüde yalnız "Excel İndir"i görür.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s3.title`, '3. Tarih aralığını seçip filtreleyin'),
      text: t(`${G}.s3.text`, 'Sayfa açılınca dünden bugüne kadar olan faturalar kendiliğinden yüklenir. Farklı bir dönem için Başlangıç ve Bitiş Tarihi seçip "Filtrele"ye basın. Arama kutusu listede belge no, ünvan gibi bilgilerle arar.'),
      tips: [
        t(`${G}.s3.tip1`, 'Şube filtresi listeyi şubeye göre daraltır; şube müdürü olarak girdiyseniz kendi şubenize kilitlidir.'),
        t(`${G}.s3.tip2`, '"Sadece okunmamış" (EDM, Gelen sekmesi) yalnız henüz açılmamış faturaları getirir.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s4.title`, '4. Gelen ve Giden sekmeleri'),
      text: t(`${G}.s4.text`, '"Gelen E-Faturalar" tedarikçilerinizden gelenleri, "Giden E-Faturalar" sizin gönderdiklerinizi listeler. Sekme değişince seçtiğiniz tarih aralığı korunur ve liste yeniden yüklenir.'),
      tips: [
        t(`${G}.s4.tip1`, 'Durum sütunu (Gelen): Yanıt Bekleniyor, Kabul Edildi, Reddedildi veya Temel Fatura.'),
        t(`${G}.s4.tip2`, '"Yeni" etiketi okunmamış faturayı, yeşil tik sisteme içeri aktarılmış faturayı gösterir.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. Faturayı açın, indirin, durumunu sorgulayın'),
      text: t(`${G}.s5.text`, 'Satırın sonundaki İşlemler (⋮) düğmesi o faturanın işlemlerini açar: Detayları Görüntüle (PavoPay\'de "PavoPay Önizleme"), İndir (PDF / HTML / UBL), Yazdır, Durum Sorgula ve Tarihçe.'),
      tips: [
        t(`${G}.s5.tip1`, 'Gelen ticari faturada yanıt bekleniyorsa "Kabul Et" / "Reddet" seçenekleri görünür.'),
        t(`${G}.s5.tip2`, 'EDM\'de gelen faturayı "Okundu / Okunmadı olarak işaretle" ile işaretleyebilirsiniz.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s6.title`, '6. Gelen faturayı içeri aktarın'),
      text: t(`${G}.s6.text`, '"İçeri Aktar" (yalnız Gelen faturalar) yeni bir alış faturası açar. Önce tedarikçi cariyle eşleştirilir, sonra fatura kalemleri stok ürünlerinizle eşleştirilip forma eklenir. Stok ve cari bakiyesi, alış faturasını kaydettiğinizde değişir.'),
      tips: [
        t(`${G}.s6.tip1`, 'Ticari faturada önce yanıt vermeniz gerekir: yanıt bekleyen fatura aktarılamaz, kabul edilen aktarılır.'),
        t(`${G}.s6.tip2`, 'Reddedilen fatura içeri aktarılamaz.'),
        t(`${G}.s6.tip3`, 'Temel fatura yanıt gerektirmez, doğrudan aktarılır.'),
        t(`${G}.s6.tip4`, 'Zaten içeri aktarılmış fatura ikinci kez aktarılamaz.'),
      ],
    },
    {
      region: 'pagination',
      title: t(`${G}.s7.title`, '7. Sayfalar arasında gezinin'),
      text: t(`${G}.s7.text`, 'Liste uzunsa alttaki sayfalamayı kullanın ve sayfa başına satır sayısını seçin. Liste boşsa "Yeni E-Fatura Oluştur" ile Satış veya Alış Faturası açabilirsiniz. Başlıktaki (i) bu rehberi yeniden açar.'),
    },
  ],
});
